import React, { useState } from 'react';
import { usePricing, ServiceType, TeamSize, Duration, SupportLevel, CustomizationLevel } from '../../context/PricingContext';
import { useCurrency } from '../../context/CurrencyContext';
import { Link } from 'react-router-dom';
import CurrencySelector from '../ui/CurrencySelector';

// Component for the pricing calculator
const PricingCalculator: React.FC = () => {
  const { params, updateParams, estimate, generateEstimate, resetCalculator } = usePricing();
  const [step, setStep] = useState<number>(1);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  // Handle service type selection
  const handleServiceTypeChange = (serviceType: ServiceType) => {
    updateParams({ serviceType });
    setStep(2);
  };

  // Handle team size selection
  const handleTeamSizeChange = (teamSize: TeamSize) => {
    updateParams({ teamSize });
    setStep(3);
  };

  // Handle duration selection
  const handleDurationChange = (duration: Duration) => {
    updateParams({ duration });
    setStep(4);
  };

  // Handle support level selection
  const handleSupportLevelChange = (supportLevel: SupportLevel) => {
    updateParams({ supportLevel });
    setStep(5);
  };

  // Handle customization level selection
  const handleCustomizationLevelChange = (customizationLevel: CustomizationLevel) => {
    updateParams({ customizationLevel });
    setStep(6);
  };

  // Handle additional services selection
  const handleAdditionalServiceChange = (service: 'includeTraining' | 'includeMaintenancePlan' | 'includePrioritySupport', checked: boolean) => {
    updateParams({ [service]: checked });
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    generateEstimate();
    setIsComplete(true);
  };

  // Handle restart
  const handleRestart = () => {
    resetCalculator();
    setStep(1);
    setIsComplete(false);
  };

  // Handle back navigation
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Get currency formatter from context
  const { formatCurrency } = useCurrency();

  // render current step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">What type of service are you interested in?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ServiceTypeCard
                title="AI Training & Education"
                description="Educational programs to help your team master AI tools"
                value="training"
                selected={params.serviceType === 'training'}
                onSelect={() => handleServiceTypeChange('training')}
              />
              <ServiceTypeCard
                title="Business AI Onboarding"
                description="Help integrating AI into your business operations"
                value="onboarding"
                selected={params.serviceType === 'onboarding'}
                onSelect={() => handleServiceTypeChange('onboarding')}
              />
              <ServiceTypeCard
                title="Workflow Automation"
                description="Automate repetitive tasks with custom AI solutions"
                value="automation"
                selected={params.serviceType === 'automation'}
                onSelect={() => handleServiceTypeChange('automation')}
              />
              <ServiceTypeCard
                title="Custom AI Development"
                description="Build specialized AI solutions for your business"
                value="development"
                selected={params.serviceType === 'development'}
                onSelect={() => handleServiceTypeChange('development')}
              />
              <ServiceTypeCard
                title="AI System Optimization"
                description="Improve and fine-tune your existing AI systems"
                value="optimization"
                selected={params.serviceType === 'optimization'}
                onSelect={() => handleServiceTypeChange('optimization')}
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">What is your team size?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <OptionCard
                title="Individual"
                description="Just you or a single person"
                selected={params.teamSize === 'individual'}
                onSelect={() => handleTeamSizeChange('individual')}
              />
              <OptionCard
                title="Small Team"
                description="2-10 people"
                selected={params.teamSize === 'small'}
                onSelect={() => handleTeamSizeChange('small')}
              />
              <OptionCard
                title="Medium Team"
                description="11-50 people"
                selected={params.teamSize === 'medium'}
                onSelect={() => handleTeamSizeChange('medium')}
              />
              <OptionCard
                title="Large Team"
                description="51-200 people"
                selected={params.teamSize === 'large'}
                onSelect={() => handleTeamSizeChange('large')}
              />
              <OptionCard
                title="Enterprise"
                description="200+ people"
                selected={params.teamSize === 'enterprise'}
                onSelect={() => handleTeamSizeChange('enterprise')}
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">What is your project timeline?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <OptionCard
                title="Short-term"
                description="Quick implementation (1-4 weeks)"
                selected={params.duration === 'short'}
                onSelect={() => handleDurationChange('short')}
              />
              <OptionCard
                title="Medium-term"
                description="Standard timeline (1-3 months)"
                selected={params.duration === 'medium'}
                onSelect={() => handleDurationChange('medium')}
              />
              <OptionCard
                title="Long-term"
                description="Comprehensive implementation (3+ months)"
                selected={params.duration === 'long'}
                onSelect={() => handleDurationChange('long')}
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">What level of support do you need?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <OptionCard
                title="Basic"
                description="Email support during business hours"
                selected={params.supportLevel === 'basic'}
                onSelect={() => handleSupportLevelChange('basic')}
              />
              <OptionCard
                title="Standard"
                description="Email and phone support with 24-hour response time"
                selected={params.supportLevel === 'standard'}
                onSelect={() => handleSupportLevelChange('standard')}
              />
              <OptionCard
                title="Premium"
                description="24/7 priority support with dedicated account manager"
                selected={params.supportLevel === 'premium'}
                onSelect={() => handleSupportLevelChange('premium')}
              />
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">What level of customization do you need?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <OptionCard
                title="Low"
                description="Standard solutions with minimal customization"
                selected={params.customizationLevel === 'low'}
                onSelect={() => handleCustomizationLevelChange('low')}
              />
              <OptionCard
                title="Medium"
                description="Tailored solutions with moderate customization"
                selected={params.customizationLevel === 'medium'}
                onSelect={() => handleCustomizationLevelChange('medium')}
              />
              <OptionCard
                title="High"
                description="Fully customized solutions built from scratch"
                selected={params.customizationLevel === 'high'}
                onSelect={() => handleCustomizationLevelChange('high')}
              />
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">Would you like to include any additional services?</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors bg-white">
                <div>
                  <h4 className="font-medium text-lg text-gray-900">Comprehensive Team Training</h4>
                  <p className="text-gray-600">In-depth training sessions to help your team master the technology</p>
                </div>
                <div className="ml-4">
                  <input
                    type="checkbox"
                    id="includeTraining"
                    checked={params.includeTraining}
                    onChange={(e) => handleAdditionalServiceChange('includeTraining', e.target.checked)}
                    className="w-5 h-5 text-blue-600 focus:ring-blue-500 rounded"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors bg-white">
                <div>
                  <h4 className="font-medium text-lg text-gray-900">Ongoing Maintenance Plan</h4>
                  <p className="text-gray-600">Regular updates, maintenance, and continuous improvement</p>
                </div>
                <div className="ml-4">
                  <input
                    type="checkbox"
                    id="includeMaintenancePlan"
                    checked={params.includeMaintenancePlan}
                    onChange={(e) => handleAdditionalServiceChange('includeMaintenancePlan', e.target.checked)}
                    className="w-5 h-5 text-blue-600 focus:ring-blue-500 rounded"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors bg-white">
                <div>
                  <h4 className="font-medium text-lg text-gray-900">Priority Support Package</h4>
                  <p className="text-gray-600">Guaranteed response times and dedicated support channel</p>
                </div>
                <div className="ml-4">
                  <input
                    type="checkbox"
                    id="includePrioritySupport"
                    checked={params.includePrioritySupport}
                    onChange={(e) => handleAdditionalServiceChange('includePrioritySupport', e.target.checked)}
                    className="w-5 h-5 text-blue-600 focus:ring-blue-500 rounded"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={handleSubmit}
                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition-colors"
              >
                Generate Estimate
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Render estimate results
  const renderEstimateResults = () => {
    if (!estimate) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Your Custom Estimate</h3>
          <CurrencySelector variant="minimal" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="text-xl font-semibold mb-4 text-gray-900">Cost Breakdown</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Service Cost</span>
                  <span className="font-medium text-gray-900">{formatCurrency(estimate.baseCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Team Size Adjustment</span>
                  <span className="font-medium text-gray-900">{formatCurrency(estimate.teamSizeCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Project Duration</span>
                  <span className="font-medium text-gray-900">{formatCurrency(estimate.durationCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Support Level</span>
                  <span className="font-medium text-gray-900">{formatCurrency(estimate.supportCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Customization</span>
                  <span className="font-medium text-gray-900">{formatCurrency(estimate.customizationCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Additional Services</span>
                  <span className="font-medium text-gray-900">{formatCurrency(estimate.additionalServicesCost)}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between font-bold">
                  <span className="text-gray-900">Estimated Total</span>
                  <span className="text-blue-600 text-xl">{formatCurrency(estimate.totalCost)}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg">
              <h4 className="text-xl font-semibold mb-4 text-gray-900">Project Details</h4>
              <div className="space-y-4">
                <div>
                  <h5 className="font-medium text-gray-900">Estimated Timeline</h5>
                  <p className="text-gray-700">{estimate.estimatedTimelineWeeks} weeks</p>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900">Recommended Services</h5>
                  <ul className="list-disc pl-5 text-gray-700">
                    {estimate.recommendedServices.map((service, index) => (
                      <li key={index}>{service}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col">
            <div className="bg-gray-50 p-6 rounded-lg mb-6 flex-grow">
              <h4 className="text-xl font-semibold mb-4 text-gray-900">Next Steps</h4>
              <p className="mb-4 text-gray-600">
                This is an estimated cost based on the information provided. For a detailed, personalized quote and project plan, we recommend scheduling a consultation with our team.
              </p>
              <p className="mb-6 text-gray-600">
                During the consultation, our experts will discuss your specific needs and provide tailored recommendations for your project.
              </p>
              <div className="space-y-4">
                <Link
                  to="/contact"
                  className="block w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg shadow text-center hover:bg-blue-700 transition-colors"
                >
                  Schedule a Consultation
                </Link>
                <button
                  type="button"
                  onClick={handleRestart}
                  className="block w-full px-6 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg text-center hover:bg-gray-50 transition-colors"
                >
                  Start Over
                </button>
              </div>
            </div>
            
            <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
              <h4 className="text-lg font-semibold mb-2 text-yellow-800">Important Notice</h4>
              <p className="text-sm text-yellow-700">
                This estimate is provided for planning purposes only and does not constitute a formal quote. Actual costs may vary based on specific requirements and project complexity. All services are subject to our terms and conditions.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-6xl mx-auto rounded-lg overflow-hidden">
      {!isComplete ? (
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Progress steps */}
          <div className="bg-gray-50 px-6 py-4 border-b">
            <div className="flex justify-between">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                      step > index + 1
                        ? 'bg-blue-600 text-white'
                        : step === index + 1
                        ? 'bg-blue-100 text-blue-600 border-2 border-blue-600'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step > index + 1 ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="text-sm font-bold">{index + 1}</span>
                    )}
                  </div>
                  <span className="text-xs mt-1 text-gray-600">
                    {index === 0
                      ? 'Service'
                      : index === 1
                      ? 'Team Size'
                      : index === 2
                      ? 'Timeline'
                      : index === 3
                      ? 'Support'
                      : index === 4
                      ? 'Customization'
                      : 'Add-ons'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {renderStepContent()}
            
            {/* Navigation buttons */}
            {step > 1 && step < 6 && (
              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>
                <div></div>
              </div>
            )}
          </form>
        </div>
      ) : (
        renderEstimateResults()
      )}
    </div>
  );
};

// Service type card component
interface ServiceTypeCardProps {
  title: string;
  description: string;
  value: string;
  selected: boolean;
  onSelect: () => void;
}

const ServiceTypeCard: React.FC<ServiceTypeCardProps> = ({ title, description, selected, onSelect }) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`p-4 border-2 rounded-lg text-left transition-all hover:shadow-md ${
        selected 
          ? 'border-blue-500 bg-blue-50 shadow-md' 
          : 'border-gray-200 hover:border-blue-300 bg-white'
      }`}
    >
      <h4 className="font-semibold text-lg mb-1 text-gray-900">{title}</h4>
      <p className="text-sm text-gray-600">{description}</p>
    </button>
  );
};

// Option card component
interface OptionCardProps {
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}

const OptionCard: React.FC<OptionCardProps> = ({ title, description, selected, onSelect }) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`p-4 border-2 rounded-lg text-left transition-all hover:shadow-md ${
        selected 
          ? 'border-blue-500 bg-blue-50 shadow-md' 
          : 'border-gray-200 hover:border-blue-300 bg-white'
      }`}
    >
      <h4 className="font-semibold text-lg mb-1 text-gray-900">{title}</h4>
      <p className="text-sm text-gray-600">{description}</p>
    </button>
  );
};

export default PricingCalculator;